import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { coursesAPI } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import ReportButton from './ReportButton'
import type { CourseReviewsPage } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n)

/**
 * A course's reviews: from students past half its lessons, one each, never
 * the instructor, and only the reviewer can remove one. The average shows
 * once there are enough reviews to mean something.
 */
const CourseReviews = ({ courseId }: { courseId: number }) => {
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const [page, setPage] = useState<CourseReviewsPage | null>(null)
  const [rating, setRating] = useState(0)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    () =>
      coursesAPI
        .reviews(courseId)
        .then(setPage)
        .catch(() => undefined),
    [courseId]
  )
  useEffect(() => {
    load()
  }, [load])

  if (!page) return null

  const send = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await coursesAPI.addReview(courseId, { rating, title: title.trim() || undefined, review_text: text.trim() || undefined })
      setRating(0)
      setTitle('')
      setText('')
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't post the review."))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: number) => {
    await coursesAPI.deleteReview(id).catch(() => undefined)
    await load()
  }

  return (
    <section aria-labelledby="course-reviews" data-testid="course-reviews">
      <h2 id="course-reviews" className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        Reviews
        <span className="ml-2 normal-case tracking-normal text-gray-400" data-testid="review-summary">
          {page.count === 0
            ? '· none yet'
            : page.average !== null
              ? `· ${page.average} of 5 from ${page.count}`
              : `· ${page.count} (an average shows from ${page.min_for_average})`}
        </span>
      </h2>

      {page.can_review ? (
        <form
          onSubmit={send}
          className="mb-4 space-y-2 rounded-xl border border-terminal-border bg-terminal-surface p-4"
          aria-label="Your review"
        >
          <div className="flex gap-1" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                aria-label={`${n} of 5`}
                onClick={() => setRating(n)}
                className={`text-xl ${n <= rating ? 'text-amber-300' : 'text-gray-600'}`}
              >
                ★
              </button>
            ))}
          </div>
          <input
            aria-label="Review title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="In a few words"
            className="w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-1.5 text-sm text-gray-100"
          />
          <textarea
            aria-label="Review"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="What did it teach you? (Reviews can't quote trading results.)"
            className="w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 text-sm text-gray-100"
          />
          <button
            type="submit"
            disabled={busy || rating === 0}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Posting…' : 'Post review'}
          </button>
          {error && (
            <p role="alert" className="text-sm text-amber-300">
              {error}
            </p>
          )}
        </form>
      ) : (
        page.why_not && (
          <p className="mb-3 text-xs text-gray-500" data-testid="review-why-not">
            {page.why_not}
          </p>
        )
      )}

      {page.reviews.length > 0 && (
        <ul className="space-y-3">
          {page.reviews.map((r) => (
            <li key={r.id} className="rounded-xl border border-terminal-border bg-terminal-surface p-4 text-sm" data-testid="review">
              <p className="flex flex-wrap items-center gap-2">
                <span className="text-amber-300" aria-label={`${r.rating} of 5`}>
                  {stars(r.rating)}
                </span>
                {r.title && <span className="font-medium text-gray-100">{r.title}</span>}
                <span className="text-xs text-gray-500">
                  {r.reviewer_username ? `@${r.reviewer_username}` : 'A former student'} ·{' '}
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
                {r.mine ? (
                  <button type="button" onClick={() => remove(r.id)} className="ml-auto text-xs text-gray-400 hover:underline">
                    Remove
                  </button>
                ) : (
                  signedIn && (
                    <span className="ml-auto">
                      <ReportButton itemType="review" itemId={r.id} />
                    </span>
                  )
                )}
              </p>
              {r.review_text && <p className="mt-1 whitespace-pre-line text-gray-300">{r.review_text}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default CourseReviews
